import { Box, Stack, styled } from '@mui/material';
import TextInput from '../../forms/TextInput';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import SelectMenu from '../../forms/SelectMenu';
import { WorkflowBaseDateTimeRef } from '../../../data/models/workflow.model';
import { lowerFirst } from 'lodash';
import {
  WaitActionTimePeriod,
  WaitAmountUnit,
  WorkflowStepFormData,
} from '../../../data/workflow-form.model';

const PREFIX = 'WorkFlowWaitStepAction';

const classes = {
  waitContainer: `${PREFIX}-waitContainer`,
  waitAmountTextInput: `${PREFIX}-waitAmountTextInput`,
  waitAmountUnitSelect: `${PREFIX}-waitAmountUnitSelect`,
  waitTimePeriodSelect: `${PREFIX}-waitTimePeriodSelect`,
  waitTimeRefSelect: `${PREFIX}-waitTimeRefSelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.waitContainer}`]: {
    gap: theme.spacing(1),
  },
  [`& .${classes.waitAmountTextInput}`]: {
    width: 180,
  },
  [`& .${classes.waitAmountUnitSelect}`]: {
    width: 200,
  },
  [`& .${classes.waitTimePeriodSelect}`]: {
    width: 180,
  },
  [`& .${classes.waitTimeRefSelect}`]: {
    minWidth: 170,
  },
}));

export default function WorkflowWaitStepAction() {
  const { t } = useTranslation('common');

  const { control, formState, getValues } =
    useFormContext<WorkflowStepFormData>();

  const getMinMaxForUnit = (unit: WaitAmountUnit) => {
    switch (unit) {
      case WaitAmountUnit.Days:
        return { min: 0, max: 99 };
      case WaitAmountUnit.Hours:
        return { min: 0, max: 24 };
      case WaitAmountUnit.Minutes:
        return { min: 0, max: 60 };
    }
  };

  const getValidateWaitRules = () => {
    const { wait } = getValues();
    if (wait) {
      const { waitAmountUnit } = wait;
      if (!waitAmountUnit) {
        return {};
      }
      const { min, max } = getMinMaxForUnit(waitAmountUnit);
      return {
        required: t('common.requiredField'),
        min: {
          value: min,
          message: t('workflows.rangeError', { min, max }),
        },
        max: {
          value: max,
          message: t('workflows.rangeError', { min, max }),
        },
      };
    }
    return {};
  };

  const waitTimePeriods = Object.values(WaitActionTimePeriod).map((value) => ({
    key: t(`workflows.${value}`),
    value,
  }));

  const waitBaseDateTimeRefs = Object.values(WorkflowBaseDateTimeRef).map(
    (value) => ({
      key: t(`workflows.${lowerFirst(value)}`),
      value,
    }),
  );

  const waitAmountUnits = Object.values(WaitAmountUnit).map((value) => ({
    key: t(`workflows.${lowerFirst(value)}`),
    value,
  }));

  useWatch({ control, name: 'wait.waitAmountUnit' });

  return (
    <StyledBox>
      <Stack direction="row" className={classes.waitContainer}>
        <TextInput
          name="wait.waitAmount"
          label={t('workflows.amount')}
          className={classes.waitAmountTextInput}
          type="number"
          control={control}
          required
          rules={getValidateWaitRules()}
          errors={formState.errors}
        />
        <SelectMenu
          name="wait.waitAmountUnit"
          label={t('workflows.unit')}
          className={classes.waitAmountUnitSelect}
          values={waitAmountUnits}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={
            formState.errors?.wait?.waitAmountUnit?.message as string
          }
          rules={{ required: t('common.requiredField') }}
        />
        <SelectMenu
          name="wait.waitTimePeriod"
          label={t('workflows.when')}
          className={classes.waitTimePeriodSelect}
          values={waitTimePeriods}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={
            formState.errors?.wait?.waitTimePeriod?.message as string
          }
          rules={{ required: t('common.requiredField') }}
        />
        <SelectMenu
          name="wait.waitBaseDateTimeRef"
          label={t('workflows.relativeTo')}
          className={classes.waitTimeRefSelect}
          values={waitBaseDateTimeRefs}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={
            formState.errors?.wait?.waitBaseDateTimeRef?.message as string
          }
          rules={{ required: t('common.requiredField') }}
        />
      </Stack>
    </StyledBox>
  );
}
